import React from 'react';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';

import EditPriceTextField from './EditPriceTextField';
import { PAY_WALL_VERSION } from './PayWallDialog';
import QuantifiableFeature from './QuantifiableFeature';
import { Feature, OnFeatureToggle } from './types';

export const useFeatureStyles = makeStyles((theme: HCPTheme) => ({
  leftIcon: {
    marginRight: 8,
  },
  rightAlign: {
    textAlign: 'right',
  },
  includedText: {
    color: theme.palette.secondary.main,
    paddingRight: theme.spacing(1),
  },
}));

export type FeatureProps = {
  feature: Feature;
  onFeatureToggle: OnFeatureToggle;
};

export type FeatureRowProps = {
  feature: Feature;
  onFeatureToggle: OnFeatureToggle;
  hasPermissions?: boolean;
};

function FeatureButton(props: FeatureProps): JSX.Element {
  const { trackButtonClicked } = useTrackEventsService();

  const { feature, onFeatureToggle } = props;
  const { featureName, enabled } = feature;

  if (enabled) {
    return (
      <Button
        variant="outlined"
        color="primary"
        onClick={() => {
          onFeatureToggle(featureName, null, false);
          trackButtonClicked({ buttonName: 'Remove paywall addon', featureName, payWallVersion: PAY_WALL_VERSION });
        }}
      >
        REMOVE
      </Button>
    );
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={() => {
        onFeatureToggle(featureName, feature.price, true);
        trackButtonClicked({
          buttonName: 'Add paywall addon',
          featureName,
          featurePrice: feature.price,
          payWallVersion: PAY_WALL_VERSION,
        });
      }}
    >
      ADD
    </Button>
  );
}

export default function AddFeatureRow(props: FeatureRowProps): JSX.Element {
  const classes = useFeatureStyles();

  const { hasPermissions = true, feature, onFeatureToggle } = props;
  const { displayName, included = false } = feature;

  if (feature.quantifiable) {
    return <QuantifiableFeature hasPermissions={hasPermissions} feature={feature} onFeatureToggle={onFeatureToggle} />;
  }

  return (
    <Grid container alignItems="center" wrap="nowrap" spacing={0} data-testid={`feature-row-${feature.featureName}`}>
      <Grid item xs={5}>
        <Typography data-testid="feature-name">{displayName}</Typography>
      </Grid>

      <Grid item xs={3} className={classes.rightAlign}>
        <EditPriceTextField feature={feature} onFeatureToggle={onFeatureToggle} />
      </Grid>

      <Grid item xs={4} className={classes.rightAlign}>
        {!included && <FeatureButton feature={feature} onFeatureToggle={onFeatureToggle} />}
        {included && (
          <Typography variant="body2" align="right" className={classes.includedText}>
            Included
          </Typography>
        )}
      </Grid>
    </Grid>
  );
}
